/**
 * 逻辑流编辑器 - React Flow 画布组件
 *
 * 策略：
 *   useNodesState 原生管理选中+拖拽 → onSelectionChange 同步选中到 Zustand
 *   结构变化（增删）→ 全量合并（保留 position+selected）
 *   数据变化（标签/配置）→ 原地更新 data（保留 position+selected）
 */

import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {
    Background,
    BackgroundVariant,
    type Connection,
    Controls,
    type DefaultEdgeOptions,
    type Edge,
    type EdgeChange,
    type FitViewOptions,
    MarkerType,
    MiniMap,
    type Node,
    type NodeChange,
    type OnConnect,
    type ProOptions,
    ReactFlow,
    useEdgesState,
    useNodesState,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {logicFlowNodeTypes, toRFNodeType} from './nodes/LogicFlowNodes';
import type {LogicFlowEdge, LogicFlowNode} from '../../types/logicFlow';

interface Props {
    lfNodes: LogicFlowNode[];
    lfEdges: LogicFlowEdge[];
    selectedNodeId: string | null;
    onSelectNode: (id: string | null) => void;
    onConnect: (edge: LogicFlowEdge) => void;
    onRemoveNode: (id: string) => void;
    onDropNewNode: (type: string, position: { x: number; y: number }) => void;
    canAddEdge: (source: string, target: string) => boolean;
}

const EDGE_STYLE: React.CSSProperties = {stroke: 'rgba(255,255,255,0.25)', strokeWidth: 2};
const EDGE_MARKER_END: DefaultEdgeOptions['markerEnd'] = {
    type: MarkerType.ArrowClosed,
    color: 'rgba(255,255,255,0.3)',
    width: 16,
    height: 16,
};
const DEFAULT_EDGE_OPTIONS: DefaultEdgeOptions = {
    type: 'smoothstep',
    animated: false,
    style: EDGE_STYLE,
    markerEnd: EDGE_MARKER_END,
};
const FIT_VIEW_OPTIONS: FitViewOptions = {padding: 0.2};
const PRO_OPTIONS: ProOptions = {hideAttribution: true};
const DELETE_KEY_CODE = ['Backspace', 'Delete'];
const WRAPPER_STYLE: React.CSSProperties = {flex: 1, height: '100%'};
const FLOW_STYLE: React.CSSProperties = {background: 'var(--pt-bg-base)'};
const PANEL_STYLE: React.CSSProperties = {
    borderRadius: '8px',
    border: '1px solid var(--pt-border)',
    background: 'rgba(12,17,32,0.95)',
};

function getMiniMapNodeColor(node: Node): string {
    switch (node.type) {
        case 'indicatorNode':
            return '#8B5CF6';
        case 'ruleNode':
            return '#F0B429';
        default:
            return 'var(--pt-text-secondary)';
    }
}

function shallowEqualEdge(left: Edge, right: Edge): boolean {
    const leftRecord = left as Record<string, unknown>;
    const rightRecord = right as Record<string, unknown>;
    const leftKeys = Object.keys(leftRecord);
    const rightKeys = Object.keys(rightRecord);

    if (leftKeys.length !== rightKeys.length) {
        return false;
    }

    return leftKeys.every(key => Object.is(leftRecord[key], rightRecord[key]));
}

function toRFNode(lfNode: LogicFlowNode, index: number): Node {
    return {
        id: lfNode.id,
        type: toRFNodeType(lfNode.type),
        position: {
            x: lfNode.x ?? 100 + (index % 4) * 250,
            y: lfNode.y ?? 150 + Math.floor(index / 4) * 180,
        },
        data: {label: lfNode.label, config: lfNode.config, nodeType: lfNode.type},
    };
}

function fromLFEdge(lfEdge: LogicFlowEdge): Edge {
    return {
        id: lfEdge.id,
        source: lfEdge.source,
        target: lfEdge.target,
        sourceHandle: lfEdge.sourceHandle || undefined,
        targetHandle: lfEdge.targetHandle || undefined,
        label: lfEdge.label,
        type: 'smoothstep',
        animated: false,
        style: EDGE_STYLE,
        markerEnd: EDGE_MARKER_END,
    };
}

const LogicFlowCanvas: React.FC<Props> = ({
                                              lfNodes,
                                              lfEdges,
                                              selectedNodeId,
                                              onSelectNode,
                                              onConnect,
                                              onRemoveNode,
                                              onDropNewNode,
                                              canAddEdge,
                                          }) => {
    const reactFlowWrapper = useRef<HTMLDivElement>(null);

    // ReactFlow 原生管理选中+拖拽
    const [rfNodes, setRFNodes, onNodesChangeNative] = useNodesState<Node>([]);
    const [rfEdges, setRFEdges, onEdgesChangeNative] = useEdgesState<Edge>([]);

    // 跑马灯动画：useMemo 从 rfEdges + selectedNodeId 计算 displayEdges
    // 关键：动画状态未变时返回 ref 缓存的同一引用，ReactFlow 浅比较跳过 StoreUpdater
    const displayEdgesRef = useRef<Edge[]>([]);
    const displayEdges = useMemo(() => {
        const cachedEdgesById = new Map(displayEdgesRef.current.map(e => [e.id, e]));
        const nextEdges = rfEdges.map(e => {
            const shouldAnimate = selectedNodeId !== null &&
                (e.source === selectedNodeId || e.target === selectedNodeId);
            const nextEdge = e.animated === shouldAnimate ? e : {...e, animated: shouldAnimate};
            const cached = cachedEdgesById.get(e.id);
            if (cached && shallowEqualEdge(cached, nextEdge)) {
                return cached;
            }
            return nextEdge;
        });

        if (displayEdgesRef.current.length === nextEdges.length) {
            const allSame = nextEdges.every((ne, i) => ne === displayEdgesRef.current[i]);
            if (allSame) {
                return displayEdgesRef.current;
            }
        }

        displayEdgesRef.current = nextEdges;
        return nextEdges;
    }, [rfEdges, selectedNodeId]);

    // 从 Zustand 同步数据到 ReactFlow
    const prevIdsRef = useRef('');
    const prevDataHashRef = useRef('');
    const prevPosHashRef = useRef('');
    useEffect(() => {
        const ids = lfNodes.map(n => n.id).sort().join(',');
        const dataHash = JSON.stringify(lfNodes.map(n => ({id: n.id, label: n.label, config: n.config})));
        const structuralChange = ids !== prevIdsRef.current;
        const dataChange = dataHash !== prevDataHashRef.current;
        const posHash = JSON.stringify(lfNodes.map(n => ({id: n.id, x: n.x, y: n.y})));
        const posChange = posHash !== prevPosHashRef.current;
        prevIdsRef.current = ids;
        prevDataHashRef.current = dataHash;
        prevPosHashRef.current = posHash;

        if (structuralChange) {
            // 增删：全量重建，保留现有节点的 position 和 selected
            setRFNodes(nds => {
                const existingMap = new Map(nds.map(n => [n.id, n]));
                return lfNodes.map((lfNode, index) => {
                    const existing = existingMap.get(lfNode.id);
                    const base = toRFNode(lfNode, index);
                    return {
                        ...base,
                        position: existing?.position || base.position,
                        selected: existing?.selected ?? false,
                    };
                });
            });
        } else if (posChange) {
            setRFNodes(nds => {
                const lfMap = new Map(lfNodes.map(n => [n.id, n]));
                return nds.map(n => {
                    const lfn = lfMap.get(n.id);
                    if (!lfn || null == lfn.x || null == lfn.y) return n;
                    if (n.position.x === lfn.x && n.position.y === lfn.y) return n;
                    return {...n, position: {x: lfn.x, y: lfn.y}};
                });
            });
        } else if (dataChange) {
            // 仅标签/配置变化：原地更新 data
            setRFNodes(nds => {
                const lfMap = new Map(lfNodes.map(n => [n.id, n]));
                let changed = false;
                const updated = nds.map(n => {
                    const lfn = lfMap.get(n.id);
                    if (!lfn) return n;
                    const labelSame = n.data.label === lfn.label;
                    const configSame = JSON.stringify(n.data.config) === JSON.stringify(lfn.config);
                    if (labelSame && configSame) return n;
                    changed = true;
                    return {...n, data: {...n.data, label: lfn.label, config: lfn.config}};
                });
                return changed ? updated : nds;
            });
        }
    }, [lfNodes, setRFNodes]);

    // 边同理
    const prevEdgeHashRef = useRef('');
    useEffect(() => {
        const edgeHash = JSON.stringify(lfEdges.map(e => ({
            id: e.id,
            source: e.source,
            target: e.target,
            sourceHandle: e.sourceHandle,
            targetHandle: e.targetHandle,
            label: e.label,
        })));
        if (edgeHash !== prevEdgeHashRef.current) {
            prevEdgeHashRef.current = edgeHash;
            setRFEdges(lfEdges.map(fromLFEdge));
        }
    }, [lfEdges, setRFEdges]);

    // onNodesChange：原生处理选中+拖拽，额外处理 remove → Zustand
    const onNodesChange = useCallback(
        (changes: NodeChange[]) => {
            onNodesChangeNative(changes);
            for (const change of changes) {
                if ('remove' === change.type) {
                    onRemoveNode(change.id);
                }
            }
        },
        [onNodesChangeNative, onRemoveNode],
    );

    // onEdgesChange：原生处理
    const onEdgesChange = useCallback(
        (changes: EdgeChange[]) => onEdgesChangeNative(changes),
        [onEdgesChangeNative],
    );

    // 连线
    const handleConnect: OnConnect = useCallback(
        (connection: Connection) => {
            if (!connection.source || !connection.target) return;
            if (!canAddEdge(connection.source, connection.target)) return;
            onConnect({
                id: `e_${connection.source}_${connection.target}_${Date.now()}`,
                source: connection.source,
                target: connection.target,
            });
        },
        [canAddEdge, onConnect],
    );

    // 选中 → Zustand
    const onSelectionChange = useCallback(
        ({nodes}: { nodes: Node[] }) => onSelectNode(nodes.length === 1 ? nodes[0].id : null),
        [onSelectNode],
    );

    const onPaneClick = useCallback(() => onSelectNode(null), [onSelectNode]);

    // 拖放
    const onDragOver = useCallback((event: React.DragEvent) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    const onDrop = useCallback(
        (event: React.DragEvent) => {
            event.preventDefault();
            const nodeType = event.dataTransfer.getData('application/logicflow-nodetype');
            if (!nodeType || !reactFlowWrapper.current) return;
            const bounds = reactFlowWrapper.current.getBoundingClientRect();
            onDropNewNode(nodeType, {
                x: event.clientX - bounds.left - 90,
                y: event.clientY - bounds.top - 30,
            });
        },
        [onDropNewNode],
    );

    return (
        <div ref={reactFlowWrapper} style={WRAPPER_STYLE}>
            <ReactFlow
                nodes={rfNodes}
                edges={displayEdges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={handleConnect}
                onSelectionChange={onSelectionChange}
                onPaneClick={onPaneClick}
                onDragOver={onDragOver}
                onDrop={onDrop}
                nodeTypes={logicFlowNodeTypes}
                fitView
                fitViewOptions={FIT_VIEW_OPTIONS}
                minZoom={0.3}
                maxZoom={2}
                deleteKeyCode={DELETE_KEY_CODE}
                defaultEdgeOptions={DEFAULT_EDGE_OPTIONS}
                proOptions={PRO_OPTIONS}
                style={FLOW_STYLE}
            >
                <Background variant={BackgroundVariant.Lines} gap={32} size={0.5} color="rgba(255,255,255,0.06)"/>
                <Controls style={PANEL_STYLE}/>
                <MiniMap
                    style={PANEL_STYLE}
                    maskColor="rgba(0,0,0,0.6)"
                    nodeColor={getMiniMapNodeColor}
                />
            </ReactFlow>
        </div>
    );
};

export default LogicFlowCanvas;
